import React, { useState } from 'react';
import { ArrowRight, Zap, Shield, Brain, Check, ChevronDown, ChevronUp, Menu, X } from 'lucide-react';
import Spline from '@splinetool/react-spline';
import { Link } from 'react-router-dom';

const Home = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI-Powered Tax Insights",
      description: "Advanced AI research engine trained on latest tax laws and regulations"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Instant Answers",
      description: "Get accurate tax answers in seconds, not hours"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Secure & Confidential",
      description: "Your financial data is encrypted and never shared"
    }
  ];

  const faqs = [
    {
      question: "How does the free plan work?",
      answer: "The free plan allows you to ask up to 3 questions to our AI tax engine. It's perfect to test our service before subscribing."
    },
    {
      question: "What's included in the subscription?",
      answer: "Unlimited questions, priority support, advanced tax planning insights, and access to all future features for ₹200/month (including all taxes)."
    },
    {
      question: "Is my financial data secure?",
      answer: "Absolutely. We use bank-level encryption and never store sensitive financial information. Your privacy is our top priority."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your subscription at any time. No questions asked, no hidden fees."
    }
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="dark-header">
        <div className="dark-logo-text">NIRAIE</div>
        <nav className="dark-nav">
          <a href="#features" className="dark-nav-link">Features</a>
          <a href="#pricing" className="dark-nav-link">Pricing</a>
          <a href="#faq" className="dark-nav-link">FAQ</a>
          <Link to="/contact" className="dark-nav-link">Contact</Link>
          <button className="btn-primary">Get Started</button>
        </nav>
        <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu">
          <a href="#features" className="mobile-menu-link" onClick={toggleMobileMenu}>Features</a>
          <a href="#pricing" className="mobile-menu-link" onClick={toggleMobileMenu}>Pricing</a>
          <a href="#faq" className="mobile-menu-link" onClick={toggleMobileMenu}>FAQ</a>
          <Link to="/contact" className="mobile-menu-link" onClick={toggleMobileMenu}>Contact</Link>
          <button className="btn-primary" style={{width: '100%'}}>Get Started</button>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-left">
            <div className="hero-badge">AI-Powered Tax Intelligence</div>
            <h1 className="hero-title">
              Future of Tax
              <br />
              <span className="hero-title-accent">Consultation</span>
            </h1>
            <p className="hero-description">
              Get instant, accurate answers to your tax questions powered by advanced AI research. 
              From filing basics to complex tax planning, Niraie has you covered.
            </p>
            <div className="hero-cta">
              <button className="btn-primary">
                Try 3 Questions Free
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="btn-secondary">Learn More</button>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">10K+</div>
                <div className="stat-label">Questions Tested</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-number">99.8%</div>
                <div className="stat-label">Accuracy Rate</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-number">24/7</div>
                <div className="stat-label">Availability</div>
              </div>
            </div>
          </div>
          <div className="hero-right">
            <div className="spline-container">
              <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-header">
          <h2 className="section-title">Why Choose Niraie</h2>
          <p className="section-subtitle">Cutting-edge AI technology meets tax expertise</p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="section-header">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">Choose the plan that works for you</p>
        </div>
        <div className="pricing-grid">
          {/* Free Plan */}
          <div className="pricing-card">
            <div className="pricing-header">
              <h3 className="pricing-plan-name">Free Trial</h3>
              <div className="pricing-amount">
                <span className="pricing-currency">₹</span>
                <span className="pricing-price">0</span>
              </div>
              <p className="pricing-period">No credit card required</p>
            </div>
            <ul className="pricing-features">
              <li className="pricing-feature">
                <Check className="w-5 h-5" />
                <span>3 Free Questions</span>
              </li>
              <li className="pricing-feature">
                <Check className="w-5 h-5" />
                <span>Basic Tax Insights</span>
              </li>
              <li className="pricing-feature">
                <Check className="w-5 h-5" />
                <span>24/7 AI Availability</span>
              </li>
            </ul>
            <button className="btn-secondary pricing-btn">Start Free Trial</button>
          </div>

          {/* Premium Plan */}
          <div className="pricing-card pricing-card-featured">
            <div className="pricing-badge">Most Popular</div>
            <div className="pricing-header">
              <h3 className="pricing-plan-name">Premium</h3>
              <div className="pricing-amount">
                <span className="pricing-currency">₹</span>
                <span className="pricing-price">200</span>
              </div>
              <p className="pricing-period">per month (including tax)</p>
            </div>
            <ul className="pricing-features">
              <li className="pricing-feature">
                <Check className="w-5 h-5" />
                <span>Unlimited Questions</span>
              </li>
              <li className="pricing-feature">
                <Check className="w-5 h-5" />
                <span>Advanced Tax Planning</span>
              </li>
              <li className="pricing-feature">
                <Check className="w-5 h-5" />
                <span>Priority Support</span>
              </li>
              <li className="pricing-feature">
                <Check className="w-5 h-5" />
                <span>Document Analysis</span>
              </li>
              <li className="pricing-feature">
                <Check className="w-5 h-5" />
                <span>Personalized Insights</span>
              </li>
            </ul>
            <button className="btn-primary pricing-btn">
              Subscribe Now
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="section-header">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Everything you need to know about Niraie</p>
        </div>
        <div className="faq-container">
          {faqs.map((faq, index) => (
            <div key={index} className="faq-item">
              <button 
                className="faq-question"
                onClick={() => toggleFaq(index)}
              >
                <span>{faq.question}</span>
                {openFaq === index ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>
              {openFaq === index && (
                <div className="faq-answer">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2 className="cta-title">Ready to Transform Your Tax Experience?</h2>
        <p className="cta-description">
          Join thousands of users who trust Niraie for their tax questions
        </p>
        <button className="btn-primary btn-large">
          Get Started Now
          <ArrowRight className="w-6 h-6" />
        </button>
      </section>

      {/* Footer */}
      <footer className="dark-footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">NIRAIE</div>
            <p className="footer-tagline">AI-Powered Tax Intelligence for Everyone</p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Product</h4>
            <ul className="footer-links">
              <li><a href="#features">Features</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Legal</h4>
            <ul className="footer-links">
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms-and-conditions">Terms & Conditions</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Support</h4>
            <ul className="footer-links">
              <li><Link to="/contact">Contact</Link></li>
              <li><a href="#">Help Center</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Niraie. All rights reserved.</p>
        </div>
      </footer>

      {/* Custom Credit Badge */}
      <div className="custom-credit">
        <span>Made with</span>
        <a href="https://sukraconsolv.com" target="_blank" rel="noopener noreferrer" className="custom-credit-link">
          SUKRA CONSOLV
        </a>
      </div>
    </div>
  );
};

export default Home;
